/**
 * Recorte de período da Composição de Saldo: ANO inteiro ou UM MÊS (competência).
 *
 * Até 18/09/2026 a tela só filtrava por ano civil fechado (`01-01` a `12-31`).
 * O mês entra ao lado, não no lugar: "Ano inteiro" continua sendo uma opção do
 * mesmo seletor, porque quem hoje concilia o exercício fechado não pode perder
 * essa leitura.
 *
 * O QUE MUDA COM O MÊS, e é o ponto que importa para quem lê a tela:
 * `data_ini` deixa de ser 1º de janeiro. Como o saldo anterior é "tudo que
 * aconteceu ANTES de `data_ini`" (`composicao_saldo._SQL_SALDO_ANTERIOR`), pedir
 * março passa a trazer jan+fev DENTRO da abertura, não na composição. É o
 * comportamento correto para competência — a composição mostra o movimento DO
 * mês, e o que veio antes vira saldo de entrada —, mas é uma mudança de
 * significado, não só de recorte.
 *
 * Datas são montadas como STRING `YYYY-MM-DD`, nunca via `toISOString()`.
 * Medido: em America/Sao_Paulo (UTC-3) o `toISOString()` NÃO desloca — soma 3h
 * e cai no mesmo dia. Ele quebra em fuso À FRENTE de UTC: com TZ=Asia/Tokyo ou
 * Europe/Lisbon no horário de verão, `new Date(2026, 2, 1).toISOString()` volta
 * `2026-02-28` e o mês inteiro anda um dia para trás. O fuso aqui é o do
 * NAVEGADOR do usuário, não o do servidor — não é variável que a gente controle.
 */

/** `null` = ano civil inteiro; 1-12 = a competência daquele mês. */
export type Competencia = number | null;

export interface PeriodoComposicao {
  data_ini: string;
  data_fim: string;
}

const dois = (n: number): string => String(n).padStart(2, '0');

/**
 * Último dia do mês. `new Date(ano, mes, 0)` devolve o dia 0 do mês SEGUINTE,
 * que é o último do mês pedido — cobre 28/29/30/31 sem tabela de dias nem
 * regra de bissexto escrita à mão (`mes` aqui é 1-12, não o 0-11 do Date).
 */
export const ultimoDiaDoMes = (ano: number, mes: number): number => new Date(ano, mes, 0).getDate();

/** O par `data_ini`/`data_fim` que a tela manda ao backend. */
export const periodoDaCompetencia = (ano: number, mes: Competencia): PeriodoComposicao =>
  mes === null
    ? { data_ini: `${ano}-01-01`, data_fim: `${ano}-12-31` }
    : {
        data_ini: `${ano}-${dois(mes)}-01`,
        data_fim: `${ano}-${dois(mes)}-${dois(ultimoDiaDoMes(ano, mes))}`,
      };

export const MESES: readonly string[] = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/** Rótulo do recorte, para cabeçalho e para o nome do arquivo exportado. */
export const rotuloCompetencia = (ano: number, mes: Competencia): string =>
  mes === null ? `${ano}` : `${MESES[mes - 1]}/${ano}`;

/**
 * Lê a competência da URL (`?mes=`). Fora de 1-12, ausente ou não-numérico cai
 * em ano inteiro — a URL é editável à mão e `?mes=13` não pode virar um mês
 * inexistente nem quebrar a tela.
 */
export const competenciaDaUrl = (valor: string | null): Competencia => {
  if (!valor) return null;
  const n = Number(valor);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : null;
};
